import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";
import { ActionPayloadValidationError, requireStringField } from "@slot/client-engine";

/**
 * GAME-DEFINED action: "detonate_chance_symbol" — the Chance (CH) feature's wire protocol. It replaced the pair
 * `present_chance_activation` + `remove_symbols_by_mask` (plan decision 42).
 *
 * A dead grid (no win) holding a CH: the CH at `sourcePositionIndex` blasts the cells in `targetMask` (its full Moore
 * neighbourhood, clipped at the grid's edges), and the blast REMOVES the source and every target. No
 * `remove_symbols_by_mask` follows; the next `collapse` drops the survivors and the refill into the hole. One
 * detonation per dead-grid event: the backend detonates the lowest-index CH, and a CH that survives into a later
 * winless grid detonates there.
 *
 * SERVER-AUTHORITATIVE: the frontend removes exactly the source and the targets it is handed — it never decides
 * whether the grid is dead or which cells go. The action carries no money and does not fire the cannon. The backend
 * and the fixtures author it on phase "win" and never skippable: it changes the grid, so a skipped detonation would
 * leave the next collapse nothing to drop from (the platform's type→phase resolver does not know game-defined types,
 * and an unknown type resolves to "other").
 */
export interface ChanceDetonationInput {
  /** The detonating CH cell: a canonical linear grid index. */
  sourcePositionIndex: string;
  /** The blasted neighbour cells, as a grid-length "0/1" mask (the source is not in it). */
  targetMask: string;
}

/** What the handler drives (the chance feature's `ChanceDetonationPresenter`). */
export interface ChanceDetonationTarget {
  detonateChanceSymbol(input: ChanceDetonationInput): Promise<void>;
}

/** Validates the payload's shape and hands it to the detonation. */
export class DetonateChanceSymbolActionHandler implements RoundActionHandler {
  public readonly actionTypeName = "detonate_chance_symbol";

  public constructor(private readonly target: ChanceDetonationTarget) {}

  public async handleAction(roundAction: RoundAction, _context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;

    const sourcePositionIndex = requireStringField(this.actionTypeName, payload, "sourcePositionIndex");
    if (!/^\d+$/.test(sourcePositionIndex)) {
      throw new ActionPayloadValidationError(this.actionTypeName, "sourcePositionIndex", `Must be a linear grid index (digits), got ${JSON.stringify(sourcePositionIndex)}.`);
    }
    const targetMask = requireStringField(this.actionTypeName, payload, "targetMask");
    if (!/^[01]+$/.test(targetMask)) {
      throw new ActionPayloadValidationError(this.actionTypeName, "targetMask", `Must be a "0/1" mask, got ${JSON.stringify(targetMask)}.`);
    }

    await this.target.detonateChanceSymbol({ sourcePositionIndex, targetMask });
  }
}
